import { useEffect, useRef, useState } from "react";
import { boundsFromPolygon, getCircleBounds } from "../../shared/geometry";
import { formatDistance } from "../../shared/filters";
import type {
  AggregateGridCell,
  Bounds,
  PlaceCard,
  PlaceSelection,
  ResolvedScoreBand,
} from "../../shared/types";
import { loadGoogleMapsApi } from "../lib/googleMaps";
import type { ResolvedTheme } from "../lib/theme";

interface MapViewProps {
  browserApiKey: string;
  mapId?: string;
  theme: ResolvedTheme;
  searchCenter: PlaceSelection | null;
  radiusMeters: number;
  scoreBands: ResolvedScoreBand[];
  searchFocusVersion: number;
  cells: AggregateGridCell[];
  places: PlaceCard[];
  selectedCellId?: string;
  showHeatLayer: boolean;
  showScoreBands: boolean;
  onViewportIdle: (bounds: Bounds, zoom: number) => void;
  onCellSelect: (cell: AggregateGridCell) => void;
  onPlaceSelected: (place: PlaceSelection) => void;
  onMapInteraction: () => void;
}

const DEFAULT_MAP_CENTER = { lat: 41.8781, lng: -87.6298 };
const DEFAULT_MAP_ZOOM = 10;
const HEATMAP_CANVAS_SIZE = 768;
const MAP_THEME_COLORS: Record<
  ResolvedTheme,
  {
    searchCircleFill: string;
    searchCircleStroke: string;
    selectedCellStroke: string;
    scoreBands: string[];
  }
> = {
  light: {
    searchCircleFill: "#ffca00",
    searchCircleStroke: "#000000",
    selectedCellStroke: "#000000",
    scoreBands: ["#ffca00", "#7a7a70", "#000000"],
  },
  dark: {
    searchCircleFill: "#ffca00",
    searchCircleStroke: "#fff4ba",
    selectedCellStroke: "#fff4ba",
    scoreBands: ["#ffca00", "#f08d3e", "#fff4ba"],
  },
};

function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported on this device."));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10_000,
      maximumAge: 60_000,
    });
  });
}

function toBoundsLiteral(bounds: Bounds): google.maps.LatLngBoundsLiteral {
  return {
    north: bounds.north,
    south: bounds.south,
    east: bounds.east,
    west: bounds.west,
  };
}

function googleBoundsToBounds(bounds: google.maps.LatLngBounds): Bounds {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  return {
    north: northEast.lat(),
    south: southWest.lat(),
    east: northEast.lng(),
    west: southWest.lng(),
  };
}

function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

function distanceMetersBetween(
  start: google.maps.LatLng,
  end: google.maps.LatLng,
): number {
  const earthRadiusMeters = 6371008.8;
  const startLat = toRadians(start.lat());
  const endLat = toRadians(end.lat());
  const deltaLat = endLat - startLat;
  const deltaLng = toRadians(end.lng() - start.lng());
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(startLat) *
      Math.cos(endLat) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadiusMeters * c;
}

function createLocationBias(bounds: google.maps.LatLngBounds) {
  const center = bounds.getCenter();
  const northEast = bounds.getNorthEast();

  return {
    center: {
      lat: center.lat(),
      lng: center.lng(),
    },
    radius: Math.min(Math.max(distanceMetersBetween(center, northEast), 5000), 50000),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function mergeBounds(boundsList: Bounds[]): Bounds | null {
  if (boundsList.length === 0) {
    return null;
  }

  return boundsList.reduce(
    (merged, bounds) => ({
      north: Math.max(merged.north, bounds.north),
      south: Math.min(merged.south, bounds.south),
      east: Math.max(merged.east, bounds.east),
      west: Math.min(merged.west, bounds.west),
    }),
    boundsList[0],
  );
}

function interpolateChannel(start: number, end: number, amount: number): number {
  return Math.round(start + (end - start) * amount);
}

function getHeatIntensity(count: number, maxCount: number): number {
  if (maxCount <= 0 || count <= 0) {
    return 0;
  }

  return clamp(Math.pow(count / maxCount, 0.72), 0.12, 1);
}

function getHeatColor(intensity: number): [number, number, number] {
  if (intensity <= 0.5) {
    const amount = intensity / 0.5;

    return [
      interpolateChannel(244, 255, amount),
      interpolateChannel(244, 202, amount),
      interpolateChannel(239, 0, amount),
    ];
  }

  const amount = (intensity - 0.5) / 0.5;

  return [
    interpolateChannel(255, 0, amount),
    interpolateChannel(202, 0, amount),
    interpolateChannel(0, 0, amount),
  ];
}

function rgba([red, green, blue]: [number, number, number], alpha: number): string {
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function createHeatmapDataUrl(cells: AggregateGridCell[], overallBounds: Bounds): string | null {
  if (typeof document === "undefined" || cells.length === 0) {
    return null;
  }

  const canvas = document.createElement("canvas");
  canvas.width = HEATMAP_CANVAS_SIZE;
  canvas.height = HEATMAP_CANVAS_SIZE;

  const context = canvas.getContext("2d");

  if (!context) {
    return null;
  }

  const maxCount = Math.max(...cells.map((cell) => cell.count), 0);
  const lngSpan = Math.max(overallBounds.east - overallBounds.west, 0.000001);
  const latSpan = Math.max(overallBounds.north - overallBounds.south, 0.000001);

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.globalCompositeOperation = "lighter";

  cells.forEach((cell) => {
    const intensity = getHeatIntensity(cell.count, maxCount);

    if (intensity <= 0) {
      return;
    }

    const bounds = boundsFromPolygon(cell.polygon);
    const x = ((cell.centroid.lng - overallBounds.west) / lngSpan) * canvas.width;
    const y = ((overallBounds.north - cell.centroid.lat) / latSpan) * canvas.height;
    const cellWidth = ((bounds.east - bounds.west) / lngSpan) * canvas.width;
    const cellHeight = ((bounds.north - bounds.south) / latSpan) * canvas.height;
    const baseRadius = Math.max(cellWidth, cellHeight) * 1.18;
    const outerRadius = clamp(baseRadius * 1.18, 82, canvas.width * 0.34);
    const innerRadius = outerRadius * 0.54;
    const color = getHeatColor(intensity);

    const outerGlow = context.createRadialGradient(x, y, 0, x, y, outerRadius);
    outerGlow.addColorStop(0, rgba(color, 0.06 + intensity * 0.08));
    outerGlow.addColorStop(0.45, rgba(color, 0.08 + intensity * 0.16));
    outerGlow.addColorStop(1, rgba(color, 0));
    context.fillStyle = outerGlow;
    context.fillRect(x - outerRadius, y - outerRadius, outerRadius * 2, outerRadius * 2);

    const hotspot = context.createRadialGradient(x, y, 0, x, y, innerRadius);
    hotspot.addColorStop(0, rgba(color, 0.14 + intensity * 0.34));
    hotspot.addColorStop(0.42, rgba(color, 0.1 + intensity * 0.22));
    hotspot.addColorStop(1, rgba(color, 0));
    context.fillStyle = hotspot;
    context.fillRect(x - innerRadius, y - innerRadius, innerRadius * 2, innerRadius * 2);
  });

  return canvas.toDataURL("image/png");
}

export function MapView({
  browserApiKey,
  mapId,
  theme,
  searchCenter,
  radiusMeters,
  scoreBands,
  searchFocusVersion,
  cells,
  places,
  selectedCellId,
  showHeatLayer,
  showScoreBands,
  onViewportIdle,
  onCellSelect,
  onPlaceSelected,
  onMapInteraction,
}: MapViewProps) {
  const mapTheme = MAP_THEME_COLORS[theme];
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const searchHostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const searchCircleRef = useRef<google.maps.Circle | null>(null);
  const scoreBandCirclesRef = useRef<google.maps.Circle[]>([]);
  const heatOverlayRef = useRef<google.maps.GroundOverlay | null>(null);
  const cellHitRegionsRef = useRef<google.maps.Rectangle[]>([]);
  const selectedCellOutlineRef = useRef<google.maps.Rectangle | null>(null);
  const placeMarkersRef = useRef<google.maps.Marker[]>([]);
  const autocompleteElementRef = useRef<Record<string, unknown> | null>(null);
  const latestViewportIdle = useRef(onViewportIdle);
  const latestCellSelect = useRef(onCellSelect);
  const latestPlaceSelected = useRef(onPlaceSelected);
  const latestMapInteraction = useRef(onMapInteraction);
  const [mapError, setMapError] = useState<string>();

  useEffect(() => {
    latestViewportIdle.current = onViewportIdle;
  }, [onViewportIdle]);

  useEffect(() => {
    latestCellSelect.current = onCellSelect;
  }, [onCellSelect]);

  useEffect(() => {
    latestPlaceSelected.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    latestMapInteraction.current = onMapInteraction;
  }, [onMapInteraction]);

  useEffect(() => {
    if (!browserApiKey || !mapElementRef.current) {
      return;
    }

    let disposed = false;
    const listeners: google.maps.MapsEventListener[] = [];

    async function initializeMap() {
      try {
        const googleMaps = await loadGoogleMapsApi(browserApiKey);
        const mapsLibrary = googleMaps.maps.importLibrary
          ? ((await googleMaps.maps.importLibrary("maps")) as google.maps.MapsLibrary)
          : ({ Map: googleMaps.maps.Map } as google.maps.MapsLibrary);

        if (disposed || !mapElementRef.current) {
          return;
        }

        const map = new mapsLibrary.Map(mapElementRef.current, {
          center: searchCenter?.center ?? DEFAULT_MAP_CENTER,
          zoom: searchCenter ? 12 : DEFAULT_MAP_ZOOM,
          mapId: mapId || undefined,
          clickableIcons: false,
          disableDefaultUI: true,
          zoomControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
        });

        mapRef.current = map;
        setMapError(undefined);
        const placesLibrary = googleMaps.maps.importLibrary
          ? await googleMaps.maps.importLibrary("places")
          : googleMaps.maps.places;
        const PlaceAutocompleteElement = (
          placesLibrary as unknown as {
            PlaceAutocompleteElement: new () => HTMLElement & Record<string, unknown>;
          }
        ).PlaceAutocompleteElement;

        if (!mapsLibrary.Map) {
          throw new Error(
            "Maps JavaScript API loaded without the Map constructor. Check that the browser key has Maps JavaScript API enabled and is allowed for this localhost origin.",
          );
        }

        if (!PlaceAutocompleteElement) {
          throw new Error(
            "Places library did not load. Check that the browser key allows Places API (New) and the current localhost origin.",
          );
        }

        if (!searchHostRef.current) {
          throw new Error("Search container is not available.");
        }

        const controlNode = document.createElement("div");
        controlNode.className = "map-search-control";

        const label = document.createElement("div");
        label.className = "map-search-control__label";
        label.textContent = "Search a city, neighborhood, or address";

        const actions = document.createElement("div");
        actions.className = "map-search-control__actions";

        const autocomplete = new PlaceAutocompleteElement();
        autocompleteElementRef.current = autocomplete;
        autocomplete.className = "map-search-control__input";
        autocomplete.setAttribute("placeholder", "Search places");
        autocomplete.setAttribute("aria-label", "Search places");

        const locationButton = document.createElement("button");
        locationButton.className = "map-search-control__geo-button";
        locationButton.type = "button";
        locationButton.setAttribute("aria-label", "Use current location");
        locationButton.title = "Use current location";
        locationButton.innerHTML = `
          <span class="map-search-control__geo-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <circle cx="12" cy="12" r="3.2"></circle>
              <path d="M12 2.5v4.1M12 17.4v4.1M21.5 12h-4.1M6.6 12H2.5"></path>
              <circle cx="12" cy="12" r="8.2"></circle>
            </svg>
          </span>
        `;

        const status = document.createElement("div");
        status.className = "map-search-control__status";

        const setStatus = (message?: string) => {
          status.textContent = message ?? "";
          status.hidden = !message;
        };

        async function handleUseMyLocation() {
          try {
            setStatus(undefined);
            locationButton.disabled = true;
            locationButton.classList.add("map-search-control__geo-button--loading");

            const position = await getCurrentPosition();
            const geocoder = new google.maps.Geocoder();
            const center = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            const response = await geocoder.geocode({ location: center });
            const labelText =
              response.results[0]?.formatted_address ?? "Current location";

            latestPlaceSelected.current({
              label: labelText,
              center,
            });
          } catch (error) {
            const message =
              error instanceof GeolocationPositionError
                ? error.code === error.PERMISSION_DENIED
                  ? "Location access was denied."
                  : "Unable to determine your current location."
                : error instanceof Error
                  ? error.message
                  : "Unable to determine your current location.";

            setStatus(message);
          } finally {
            locationButton.disabled = false;
            locationButton.classList.remove("map-search-control__geo-button--loading");
          }
        }

        locationButton.addEventListener("click", () => {
          void handleUseMyLocation();
        });

        actions.append(autocomplete, locationButton);
        controlNode.append(label, actions, status);
        searchHostRef.current.replaceChildren(controlNode);

        autocomplete.addEventListener("gmp-select", async (event: Event) => {
          const customEvent = event as Event & {
            placePrediction?: {
              toPlace?: () => PromiseLike<Record<string, unknown>> | Record<string, unknown>;
            };
          };
          const prediction = customEvent.placePrediction;

          if (!prediction?.toPlace) {
            return;
          }

          const place = await prediction.toPlace();
          const fetchFields = (place as { fetchFields?: (input: { fields: string[] }) => Promise<void> })
            .fetchFields;

          if (fetchFields) {
            await fetchFields.call(place, {
              fields: ["displayName", "location", "id"],
            });
          }

          const location = (place as { location?: google.maps.LatLng }).location;

          if (!location) {
            return;
          }

          latestPlaceSelected.current({
            label:
              ((place as { displayName?: string }).displayName ??
                (place as { name?: string }).name ??
                "Selected place"),
            center: {
              lat: location.lat(),
              lng: location.lng(),
            },
            placeId: (place as { id?: string }).id,
          });
        });

        listeners.push(
          map.addListener("idle", () => {
            const bounds = map.getBounds();

            if (!bounds) {
              return;
            }

            if (autocompleteElementRef.current) {
              (
                autocompleteElementRef.current as {
                  locationBias?: {
                    center: { lat: number; lng: number };
                    radius: number;
                  } | null;
                }
              ).locationBias = createLocationBias(bounds);
            }

            latestViewportIdle.current(googleBoundsToBounds(bounds), map.getZoom() ?? DEFAULT_MAP_ZOOM);
          }),
        );
        listeners.push(
          map.addListener("dragstart", () => {
            latestMapInteraction.current();
          }),
        );
        listeners.push(
          map.addListener("zoom_changed", () => {
            latestMapInteraction.current();
          }),
        );
      } catch (error) {
        if (!disposed) {
          setMapError(error instanceof Error ? error.message : "Map failed to load.");
        }
      }
    }

    void initializeMap();

    return () => {
      disposed = true;
      listeners.forEach((listener) => listener.remove());
      heatOverlayRef.current?.setMap(null);
      cellHitRegionsRef.current.forEach((rectangle) => rectangle.setMap(null));
      selectedCellOutlineRef.current?.setMap(null);
      placeMarkersRef.current.forEach((marker) => marker.setMap(null));
      scoreBandCirclesRef.current.forEach((circle) => circle.setMap(null));
      searchCircleRef.current?.setMap(null);
      searchHostRef.current?.replaceChildren();
      mapRef.current = null;
    };
  }, [browserApiKey, mapId]);

  useEffect(() => {
    const map = mapRef.current;

    if (!map || !searchCenter) {
      return;
    }

    const bounds = getCircleBounds(searchCenter.center, radiusMeters);
    map.fitBounds(toBoundsLiteral(bounds), 48);
  }, [searchCenter, radiusMeters, searchFocusVersion]);

  useEffect(() => {
    const map = mapRef.current;

    if (!map) {
      return;
    }

    if (!searchCenter) {
      searchCircleRef.current?.setMap(null);
      searchCircleRef.current = null;
      return;
    }

    if (!searchCircleRef.current) {
      searchCircleRef.current = new google.maps.Circle({
        map,
        fillColor: mapTheme.searchCircleFill,
        fillOpacity: 0.06,
        strokeColor: mapTheme.searchCircleStroke,
        strokeOpacity: 0.7,
        strokeWeight: 2,
      });
    }

    searchCircleRef.current.setCenter(searchCenter.center);
    searchCircleRef.current.setRadius(radiusMeters);
    searchCircleRef.current.setOptions({
      fillColor: mapTheme.searchCircleFill,
      strokeColor: mapTheme.searchCircleStroke,
      strokeOpacity: showScoreBands ? 0.18 : 0.7,
      strokeWeight: showScoreBands ? 1.2 : 2,
    });
    searchCircleRef.current.setMap(map);
  }, [mapTheme.searchCircleFill, mapTheme.searchCircleStroke, radiusMeters, searchCenter, showScoreBands]);

  useEffect(() => {
    const map = mapRef.current;

    scoreBandCirclesRef.current.forEach((circle) => circle.setMap(null));
    scoreBandCirclesRef.current = [];

    if (!map || !searchCenter || !showScoreBands) {
      return;
    }

    const bandCircles = scoreBands.map(
      (band, index) =>
        new google.maps.Circle({
          center: searchCenter.center,
          radius: band.radiusMeters,
          map,
          clickable: false,
          fillOpacity: 0,
          strokeColor:
            mapTheme.scoreBands[index] ??
            mapTheme.scoreBands[mapTheme.scoreBands.length - 1],
          strokeOpacity: 0.9,
          strokeWeight: index === scoreBands.length - 1 ? 2.2 : 1.6,
        }),
    );

    scoreBandCirclesRef.current = bandCircles;

    return () => {
      bandCircles.forEach((circle) => circle.setMap(null));
    };
  }, [mapTheme.scoreBands, scoreBands, searchCenter, showScoreBands]);

  useEffect(() => {
    const map = mapRef.current;

    heatOverlayRef.current?.setMap(null);
    heatOverlayRef.current = null;
    cellHitRegionsRef.current.forEach((rectangle) => rectangle.setMap(null));
    cellHitRegionsRef.current = [];
    selectedCellOutlineRef.current?.setMap(null);
    selectedCellOutlineRef.current = null;

    if (!map || !showHeatLayer || cells.length === 0) {
      return;
    }

    const cellBounds = cells.map((cell) => boundsFromPolygon(cell.polygon));
    const overallBounds = mergeBounds(cellBounds);

    if (!overallBounds) {
      return;
    }

    const heatmapUrl = createHeatmapDataUrl(cells, overallBounds);

    if (heatmapUrl) {
      const heatOverlay = new google.maps.GroundOverlay(heatmapUrl, toBoundsLiteral(overallBounds), {
        opacity: 0.92,
      });
      heatOverlay.setMap(map);
      heatOverlayRef.current = heatOverlay;
    }

    const hitRegions = cells.map((cell) => {
      const bounds = boundsFromPolygon(cell.polygon);
      const rectangle = new google.maps.Rectangle({
        bounds: toBoundsLiteral(bounds),
        fillOpacity: 0.001,
        map,
        strokeOpacity: 0,
        strokeWeight: 0,
      });

      rectangle.addListener("click", () => {
        latestCellSelect.current(cell);
      });

      return rectangle;
    });

    cellHitRegionsRef.current = hitRegions;

    const selectedCell = cells.find((cell) => cell.id === selectedCellId);

    if (selectedCell) {
      selectedCellOutlineRef.current = new google.maps.Rectangle({
        bounds: toBoundsLiteral(boundsFromPolygon(selectedCell.polygon)),
        fillOpacity: 0,
        map,
        strokeColor: mapTheme.selectedCellStroke,
        strokeOpacity: 0.92,
        strokeWeight: 2.4,
      });
    }

    return () => {
      heatOverlayRef.current?.setMap(null);
      heatOverlayRef.current = null;
      hitRegions.forEach((rectangle) => rectangle.setMap(null));
      selectedCellOutlineRef.current?.setMap(null);
      selectedCellOutlineRef.current = null;
    };
  }, [cells, mapTheme.selectedCellStroke, selectedCellId, showHeatLayer]);

  useEffect(() => {
    const map = mapRef.current;

    if (!map) {
      return;
    }

    placeMarkersRef.current.forEach((marker) => marker.setMap(null));
    const markers = places.map(
      (place) =>
        new google.maps.Marker({
          map,
          position: {
            lat: place.lat,
            lng: place.lng,
          },
          title: place.name,
        }),
    );

    placeMarkersRef.current = markers;

    return () => {
      markers.forEach((marker) => marker.setMap(null));
    };
  }, [places]);

  return (
    <div className="map-frame">
      <div className="map-search-host" ref={searchHostRef} />
      <div className="map-canvas" ref={mapElementRef} />
      {showHeatLayer && cells.length > 0 ? (
        <div className="map-overlay map-overlay--right map-overlay--bottom">
          <div className="heat-legend">
            <p className="eyebrow">Heat layer</p>
            <div className="heat-legend__bar" />
            <div className="heat-legend__labels">
              <span>Lower density</span>
              <span>Higher density</span>
            </div>
          </div>
        </div>
      ) : null}
      {showScoreBands && searchCenter ? (
        <div className="map-overlay map-overlay--right map-overlay--bottom">
          <div className="band-legend">
            <p className="eyebrow">Score bands</p>
            <div className="band-legend__items">
              {scoreBands.map((band, index) => (
                <div className="band-legend__item" key={band.radiusMeters}>
                  <span
                    aria-hidden="true"
                    className="band-legend__swatch"
                    style={{
                      backgroundColor:
                        mapTheme.scoreBands[index] ??
                        mapTheme.scoreBands[mapTheme.scoreBands.length - 1],
                    }}
                  />
                  <span>{formatDistance(band.radiusMeters)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      {mapError ? (
        <div className="map-fallback">
          <p className="eyebrow">Map error</p>
          <h2>{mapError}</h2>
          <p>Check your browser-facing Google Maps API key and Places library access.</p>
        </div>
      ) : null}
    </div>
  );
}
